import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  ChartLineUp,
  CurrencyDollar,
  Sun,
  ArrowRight,
  SignOut,
  CheckCircle,
  X,
} from "@phosphor-icons/react";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import Header from "../components/Header";
import { apiClient, getStoredToken, setAuthToken } from "../lib/api";
import { toast } from "sonner";

const PALETTE = ["#0EA5E9", "#F59E0B", "#10B981", "#8B5CF6", "#64748B"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);

  useEffect(() => {
    if (!getStoredToken()) {
      navigate("/admin/login");
      return;
    }
    Promise.all([
      apiClient.get("/admin/leads").then((r) => setLeads(r.data)),
      apiClient.get("/admin/analytics").then((r) => setAnalytics(r.data)),
    ])
      .catch((e) => {
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          setAuthToken(null);
          navigate("/admin/login");
        } else {
          toast.error("Could not load dashboard");
        }
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const logout = () => {
    setAuthToken(null);
    navigate("/admin/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header variant="default" />
        <div className="pt-32 text-center text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header variant="default" />
      <div className="pt-20 max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="font-display text-4xl font-bold text-slate-950 tracking-tight">
              Lead Dashboard
            </h1>
            <p className="text-slate-500 mt-1">Maui Starpower Network · HI Power Savings AI</p>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="btn-secondary inline-flex items-center gap-2"
          >
            <SignOut size={18} /> Logout
          </button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={Users}
            color="sky"
            label="Total Leads"
            value={analytics?.totals?.leads ?? 0}
            sub={`${analytics?.totals?.ghl_success ?? 0} synced to GHL`}
          />
          <StatCard
            icon={ChartLineUp}
            color="amber"
            label="Bills Analyzed"
            value={analytics?.totals?.extractions ?? 0}
            sub={`${analytics?.totals?.conversion_rate ?? 0}% conversion`}
          />
          <StatCard
            icon={Sun}
            color="emerald"
            label="Avg System"
            value={`${analytics?.averages?.system_kw ?? 0} kW`}
            sub={`$${(analytics?.averages?.system_value ?? 0).toLocaleString()} avg`}
          />
          <StatCard
            icon={CurrencyDollar}
            color="violet"
            label="Pipeline Value"
            value={`$${((analytics?.pipeline_value ?? 0) / 1000).toFixed(0)}k`}
            sub="Estimated installed"
          />
        </div>

        {/* Charts */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <div className="card-clean p-8">
            <h3 className="font-display text-lg font-semibold text-slate-900 mb-1">Leads by island</h3>
            <p className="text-sm text-slate-500 mb-6">Geographic distribution of pilot leads</p>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={analytics?.by_island ?? []}>
                <XAxis dataKey="name" stroke="#94A3B8" tick={{ fontSize: 12 }} />
                <YAxis stroke="#94A3B8" tick={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
                <Bar dataKey="value" fill="#0EA5E9" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="card-clean p-8">
            <h3 className="font-display text-lg font-semibold text-slate-900 mb-1">By interest</h3>
            <p className="text-sm text-slate-500 mb-6">Solar / HVAC / Whole-home retrofit</p>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={analytics?.by_interest ?? []}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={90}
                  paddingAngle={2}
                >
                  {(analytics?.by_interest ?? []).map((_, i) => (
                    <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Leads table */}
        <div className="card-clean overflow-hidden">
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold text-slate-900">Recent leads</h3>
            <span className="text-sm text-slate-500">{leads.length} total</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider font-semibold">
                  <th className="text-left p-4">Name</th>
                  <th className="text-left p-4">Contact</th>
                  <th className="text-left p-4">Island</th>
                  <th className="text-left p-4">Interest</th>
                  <th className="text-right p-4">Est. System</th>
                  <th className="text-right p-4">Est. Value</th>
                  <th className="text-center p-4">GHL</th>
                  <th className="text-center p-4"></th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center p-12 text-slate-500">
                      No leads yet. Once customers complete the wizard, they'll appear here.
                    </td>
                  </tr>
                ) : (
                  leads.map((l) => (
                    <tr
                      key={l.id}
                      className="border-t border-slate-100 hover:bg-slate-50/50 transition-colors"
                      data-testid={`lead-row-${l.id}`}
                    >
                      <td className="p-4 font-semibold text-slate-900">{l.full_name}</td>
                      <td className="p-4 text-slate-600">
                        <div>{l.email}</div>
                        <div className="text-xs text-slate-500">{l.phone}</div>
                      </td>
                      <td className="p-4 text-slate-700">{l.island || "—"}</td>
                      <td className="p-4">
                        <span className="text-xs font-semibold uppercase tracking-wider px-2 py-1 rounded-full bg-sky-50 text-sky-700">
                          {l.interest_type}
                        </span>
                      </td>
                      <td className="p-4 text-right font-mono text-slate-700">
                        {l.estimated_system_kw ? `${l.estimated_system_kw} kW` : "—"}
                      </td>
                      <td className="p-4 text-right font-mono text-slate-700">
                        {l.estimated_system_cost
                          ? `$${l.estimated_system_cost.toLocaleString()}`
                          : "—"}
                      </td>
                      <td className="p-4 text-center">
                        {l.ghl_status === "success" ? (
                          <CheckCircle size={20} weight="fill" className="text-emerald-500 inline" />
                        ) : (
                          <X size={20} className="text-slate-300 inline" />
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <button
                          data-testid={`lead-view-${l.id}`}
                          onClick={() => setSelectedLead(l)}
                          className="text-sky-600 font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all"
                        >
                          View <ArrowRight size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Lead detail drawer */}
      {selectedLead && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setSelectedLead(null)}
          data-testid="lead-detail-modal"
        >
          <div
            className="bg-white rounded-3xl max-w-2xl w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="text-xs uppercase tracking-wider text-sky-600 font-bold mb-1">
                    Lead details
                  </div>
                  <h2 className="font-display text-3xl font-bold text-slate-950">
                    {selectedLead.full_name}
                  </h2>
                  <p className="text-slate-500 mt-1">{selectedLead.email}</p>
                </div>
                <button
                  onClick={() => setSelectedLead(null)}
                  data-testid="lead-detail-close"
                  className="p-2 hover:bg-slate-100 rounded-full"
                >
                  <X size={22} className="text-slate-500" />
                </button>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <DetailRow label="Phone" value={selectedLead.phone} />
                <DetailRow label="Best time" value={selectedLead.best_time_to_call} />
                <DetailRow label="Address" value={`${selectedLead.street || ""} ${selectedLead.city || ""} ${selectedLead.zip || ""}`.trim() || "—"} />
                <DetailRow label="Island" value={selectedLead.island || "—"} />
                <DetailRow label="Utility" value={selectedLead.utility_provider || "—"} />
                <DetailRow label="Interest" value={selectedLead.interest_type} />
                <DetailRow label="Energy Score" value={selectedLead.energy_score ? `${selectedLead.energy_score}/100` : "—"} />
                <DetailRow label="Monthly kWh" value={selectedLead.monthly_kwh ? selectedLead.monthly_kwh.toString() : "—"} />
              </div>

              <div className="rounded-2xl bg-gradient-to-br from-slate-950 to-slate-800 text-white p-6 mb-6">
                <div className="text-xs uppercase tracking-[0.2em] text-amber-400 font-bold mb-3">
                  Recommended system
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-xs text-slate-400">Size</div>
                    <div className="font-display text-2xl font-bold">
                      {selectedLead.estimated_system_kw} kW
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400">Battery</div>
                    <div className="font-display text-2xl font-bold">
                      {selectedLead.estimated_battery_kwh} kWh
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400">Net cost</div>
                    <div className="font-display text-2xl font-bold">
                      ${(selectedLead.estimated_system_cost || 0).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>

              {selectedLead.notes && (
                <div className="bg-slate-50 rounded-2xl p-5 mb-6">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                    Notes
                  </div>
                  <p className="text-slate-700">{selectedLead.notes}</p>
                </div>
              )}

              <div className="text-xs text-slate-500 border-t border-slate-100 pt-4 space-y-1">
                <div>
                  <strong>Created:</strong> {new Date(selectedLead.created_at).toLocaleString()}
                </div>
                <div>
                  <strong>GHL Status:</strong>{" "}
                  <span className={selectedLead.ghl_status === "success" ? "text-emerald-600 font-semibold" : "text-slate-700"}>
                    {selectedLead.ghl_status}
                  </span>
                  {selectedLead.ghl_contact_id && (
                    <span className="ml-2">· Contact ID: {selectedLead.ghl_contact_id}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value, sub }) {
  const colorMap = {
    sky: "from-sky-500 to-sky-600",
    amber: "from-amber-500 to-amber-600",
    emerald: "from-emerald-500 to-emerald-600",
    violet: "from-violet-500 to-violet-600",
  };
  return (
    <div className="card-clean p-6">
      <div
        className={`w-11 h-11 rounded-xl bg-gradient-to-br ${colorMap[color]} flex items-center justify-center mb-4`}
      >
        <Icon size={22} weight="duotone" className="text-white" />
      </div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="font-display text-3xl font-bold text-slate-900 mt-1">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{sub}</div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1">
        {label}
      </div>
      <div className="text-slate-900 font-medium">{value || "—"}</div>
    </div>
  );
}
